import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface ScheduledWorkflow {
  id: string;
  workflowName: string;
  frequency: 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly';
  time: string;
  enabled: boolean;
  nextRun?: Date;
}

export function WorkflowScheduler() {
  const [schedules, setSchedules] = useState<ScheduledWorkflow[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    workflowName: '',
    frequency: 'daily' as const,
    time: '09:00',
  });

  const handleAddSchedule = () => {
    if (!formData.workflowName || !formData.time) {
      toast.error('Please fill in all fields');
      return;
    }

    const newSchedule: ScheduledWorkflow = {
      id: Math.random().toString(36).substr(2, 9),
      workflowName: formData.workflowName,
      frequency: formData.frequency,
      time: formData.time,
      enabled: true,
    };

    setSchedules([...schedules, newSchedule]);
    setFormData({ workflowName: '', frequency: 'daily', time: '09:00' });
    setIsOpen(false);
    toast.success('Schedule created');
  };

  const handleDeleteSchedule = (id: string) => {
    setSchedules(schedules.filter((s) => s.id !== id));
    toast.success('Schedule deleted');
  };

  const handleToggleSchedule = (id: string) => {
    setSchedules(
      schedules.map((s) =>
        s.id === id ? { ...s, enabled: !s.enabled } : s
      )
    );
  };

  const frequencyLabels = {
    once: 'Once',
    hourly: 'Every Hour',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Workflow Scheduler</h3>
          <p className="text-sm text-muted-foreground">Schedule workflows to run automatically</p>
        </div>
        <Button size="sm" onClick={() => setIsOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Schedule
        </Button>
      </div>

      {/* Schedules List */}
      {schedules.length === 0 ? (
        <Card className="p-6 text-center">
          <Clock className="w-12 h-12 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">
            No schedules yet. Create one to automate your workflows.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {schedules.map((schedule) => (
            <Card
              key={schedule.id}
              className={`p-4 transition-all ${schedule.enabled ? 'border-green-500/20 bg-green-500/5' : 'opacity-50'}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground">{schedule.workflowName}</h4>
                  <div className="flex gap-3 mt-2 text-sm text-muted-foreground">
                    <span>
                      {frequencyLabels[schedule.frequency]} at {schedule.time}
                    </span>
                    <span
                      className={`font-medium ${
                        schedule.enabled ? 'text-green-600' : 'text-amber-600'
                      }`}
                    >
                      {schedule.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={schedule.enabled ? 'outline' : 'default'}
                    size="sm"
                    onClick={() => handleToggleSchedule(schedule.id)}
                  >
                    {schedule.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDeleteSchedule(schedule.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New Schedule Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Schedule</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="workflow-name">Workflow Name</Label>
              <Input
                id="workflow-name"
                placeholder="Enter workflow name"
                value={formData.workflowName}
                onChange={(e) =>
                  setFormData({ ...formData, workflowName: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="frequency">Frequency</Label>
              <Select value={formData.frequency} onValueChange={(value: any) => 
                setFormData({ ...formData, frequency: value })
              }>
                <SelectTrigger id="frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Once</SelectItem>
                  <SelectItem value="hourly">Every Hour</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="time">Time</Label>
              <Input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) =>
                  setFormData({ ...formData, time: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSchedule}>Create Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
